import React, { useEffect, useMemo, useState } from 'react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Icon from '../../../components/AppIcon';
import { api } from '../../../lib/api';
import { useAuth } from '../../../context/AuthContext';
import { parseApiError } from '../utils';

type CatalogStructure = 'semestru' | 'trimestru' | 'module' | string;
type EducationLevel = 'primar' | 'gimnazial' | 'liceal' | 'universitar' | string;

type Grade = {
  id: number;
  student_id: number;
  subject_id: number | null;
  teacher_id?: number | null;
  value?: number | null;
  qualifier?: string | null;
  comment?: string | null;
  period_index?: number | null;
  gradebook_config_id?: number | null;
  date?: string | null;
  created_at?: string | null;
  is_thesis?: boolean;
  is_homework?: boolean;
  is_lab?: boolean;
  is_seminar?: boolean;
  is_final?: boolean;
  is_conduct?: boolean;
  category_id?: number | null;
  category_name?: string | null;
};

type Absence = {
  id: number;
  student_id: number;
  subject_id: number | null;
  classroom_id: number;
  gradebook_config_id?: number | null;
  period_index?: number | null;
  date?: string | null;
  justified?: string | null;
  justification_reason?: string | null;
  created_at?: string | null;
};

type StudentLite = { id: number; full_name: string; email?: string | null };
type SubjectLite = { id: number; name: string; professor_id?: number | null };

type GradeType = 'nota' | 'teza' | 'calificativ' | 'tema' | 'laborator' | 'seminar' | 'finala' | 'purtare';

interface Props {
  open: boolean;
  onClose: () => void;
  classroomId: number;
  configId: number;
  structure: CatalogStructure;
  educationLevel: EducationLevel;
  periodIndex: number;
  student: StudentLite;
  subject: SubjectLite | null; // null => purtare
  onChanged: () => Promise<void> | void;
}

const toBackendDateTime = (d: string) => {
  // input type=date => YYYY-MM-DD
  // IMPORTANT: evităm toISOString() (UTC) care poate muta ziua cu -1/+1 în funcție de fus orar.
  // Trimitem un datetime "naive" în ora locală, ca să păstreze ziua selectată.
  if (!d) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(d);
  if (!m) return null;
  return `${m[1]}-${m[2]}-${m[3]}T12:00:00`;
};

const isJustified = (v?: string | null) => (v || 'nu').toLowerCase() === 'da';

const gradeLabel = (g: Grade) => {
  const base = g.value != null ? String(g.value) : (g.qualifier || '');
  if (!base) return '—';
  const tags: string[] = [];
  if (g.is_thesis) tags.push('teză');
  if (g.is_homework) tags.push('temă');
  if (g.is_lab) tags.push('laborator');
  if (g.is_seminar) tags.push('seminar');
  if (g.is_final) tags.push('finală');
  if (g.is_conduct) tags.push('purtare');
  const tagTxt = tags.length ? ` (${tags.join(', ')})` : '';
  return `${base}${tagTxt}`;
};

const avgOf = (nums: number[]) => {
  if (nums.length === 0) return null;
  const a = nums.reduce((s, v) => s + v, 0) / nums.length;
  return Math.round(a * 100) / 100;
};

const splitByKind = (grades: Grade[]) => {
  const thesis = grades.filter((g) => g.is_thesis);
  const homework = grades.filter((g) => g.is_homework);
  const lab = grades.filter((g) => g.is_lab);
  const seminar = grades.filter((g) => g.is_seminar);
  const final = grades.filter((g) => g.is_final);
  const conduct = grades.filter((g) => g.is_conduct);
  const normal = grades.filter(
    (g) =>
      !g.is_thesis &&
      !g.is_homework &&
      !g.is_lab &&
      !g.is_seminar &&
      !g.is_final &&
      !g.is_conduct
  );
  return { normal, thesis, homework, lab, seminar, final, conduct };
};

const calcWeightedAverageWithThesis = (grades: Grade[]): number | null => {
  const { normal, thesis } = splitByKind(grades);
  const normalNums = normal.map((g) => g.value).filter((v): v is number => typeof v === 'number');
  const thesisNums = thesis.map((g) => g.value).filter((v): v is number => typeof v === 'number');
  const avgNormal = avgOf(normalNums);
  const avgThesis = avgOf(thesisNums);
  if (avgNormal != null && avgThesis != null) return Math.round(((avgNormal * 3 + avgThesis) / 4) * 100) / 100;
  if (avgNormal != null) return avgNormal;
  if (avgThesis != null) return avgThesis;
  return null;
};

const calcUniversityAverage = (grades: Grade[]): number | null => {
  const { homework, final } = splitByKind(grades);
  const excludedIds = new Set<number>([...homework.map((g) => g.id), ...final.map((g) => g.id)]);
  const nums = grades
    .filter((g) => !excludedIds.has(g.id))
    .map((g) => g.value)
    .filter((v): v is number => typeof v === 'number');
  return avgOf(nums);
};

const allowedTypes = (level: EducationLevel, subject: SubjectLite | null): GradeType[] => {
  if (!subject) return ['purtare'];
  if (level === 'primar') return ['calificativ', 'tema'];
  if (level === 'universitar') return ['nota', 'laborator', 'seminar', 'finala', 'tema'];
  return ['nota', 'teza', 'tema'];
};

const CatalogCellModal: React.FC<Props> = ({
  open,
  onClose,
  classroomId,
  configId,
  structure,
  educationLevel,
  periodIndex,
  student,
  subject,
  onChanged,
}) => {
  const { me } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [grades, setGrades] = useState<Grade[]>([]);
  const [absences, setAbsences] = useState<Absence[]>([]);

  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [formType, setFormType] = useState<GradeType>('nota');
  const [formValue, setFormValue] = useState('');
  const [formQualifier, setFormQualifier] = useState('');
  const [formDate, setFormDate] = useState('');
  const [formComment, setFormComment] = useState('');
  const [formCategoryName, setFormCategoryName] = useState('');

  const [absenceDate, setAbsenceDate] = useState('');
  const [absenceJustified, setAbsenceJustified] = useState(false);
  const [showJustifyModal, setShowJustifyModal] = useState(false);
  const [justifyTarget, setJustifyTarget] = useState<Absence | null>(null);
  const [justifyReason, setJustifyReason] = useState('');

  const [editingGrade, setEditingGrade] = useState<Grade | null>(null);

  const typeOptions = useMemo(() => allowedTypes(educationLevel, subject), [educationLevel, subject]);
  const canEdit = useMemo(() => {
    if (!subject) return true;
    if (!me?.id) return false;
    if (subject.professor_id == null) return false;
    return Number(subject.professor_id) === Number(me.id);
  }, [me?.id, subject]);
  const average = useMemo(() => {
    if (!subject) return null;
    if (educationLevel === 'primar') return null;
    if (educationLevel === 'universitar') return calcUniversityAverage(grades);
    return calcWeightedAverageWithThesis(grades);
  }, [educationLevel, grades, subject]);

  const resetForm = () => {
    setFormError(null);
    setEditingGrade(null);
    const first = typeOptions[0] || 'nota';
    setFormType(first);
    setFormValue('');
    setFormQualifier('');
    setFormDate('');
    setFormComment('');
    setFormCategoryName('');
  };

  const resetAbsenceForm = () => {
    setAbsenceDate('');
    setAbsenceJustified(false);
  };

  const loadData = async () => {
    if (!open) return;
    setLoading(true);
    setError(null);
    try {
      const gradesResp = await api.get<Grade[]>(`/api/grades/student/${student.id}`, {
        params: {
          gradebook_config_id: configId,
          period_index: periodIndex,
          ...(subject ? { subject_id: subject.id } : {}),
        },
      });

      const rawGrades = Array.isArray(gradesResp.data) ? gradesResp.data : [];
      const filteredGrades = subject ? rawGrades : rawGrades.filter((g) => g.is_conduct);
      setGrades(filteredGrades);

      if (subject) {
        const absResp = await api.get<Absence[]>(`/api/absente/student/${student.id}`, {
          params: { gradebook_config_id: configId, period_index: periodIndex },
        });
        const rawAbs = Array.isArray(absResp.data) ? absResp.data : [];
        setAbsences(rawAbs.filter((a) => a.subject_id === subject.id));
      } else {
        setAbsences([]);
      }
    } catch (err: any) {
      setError(parseApiError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!open) return;
    resetForm();
    resetAbsenceForm();
    void loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, student.id, subject?.id, configId, periodIndex]);

  const startEditGrade = (g: Grade) => {
    setEditingGrade(g);
    setFormError(null);

    // infer type
    let t: GradeType = 'nota';
    if (g.is_conduct) t = 'purtare';
    else if (g.is_thesis) t = 'teza';
    else if (g.is_final) t = 'finala';
    else if (g.is_lab) t = 'laborator';
    else if (g.is_seminar) t = 'seminar';
    else if (g.is_homework) t = 'tema';
    else if (g.qualifier) t = 'calificativ';

    setFormType(t);
    setFormValue(g.value != null ? String(g.value) : '');
    setFormQualifier(g.qualifier || '');
    setFormComment(g.comment || '');
    setFormCategoryName(g.category_name || '');
    const iso = (g.date || g.created_at) as string | null;
    if (iso) {
      const d = new Date(iso);
      if (!isNaN(d.getTime())) {
        const yyyy = d.getFullYear();
        const mm = String(d.getMonth() + 1).padStart(2, '0');
        const dd = String(d.getDate()).padStart(2, '0');
        setFormDate(`${yyyy}-${mm}-${dd}`);
      }
    }
  };

  const submitGrade = async () => {
    setFormError(null);
    if (!canEdit) {
      setFormError('Poti adauga/edita note doar la materiile pe care le predai.');
      return;
    }
    const isCalif = educationLevel === 'primar' || formType === 'calificativ';
    const isConduct = formType === 'purtare' || !subject;

    if (!isConduct && !subject) {
      setFormError('Selectează o materie.');
      return;
    }

    if (isCalif) {
      if (!formQualifier.trim()) {
        setFormError('Alege calificativul.');
        return;
      }
    } else {
      const v = Number(formValue);
      if (!formValue.trim() || Number.isNaN(v)) {
        setFormError('Completează nota numerică.');
        return;
      }
      if (v < 1 || v > 10) {
        setFormError('Nota trebuie să fie între 1 și 10.');
        return;
      }
    }

    const iso = formDate ? toBackendDateTime(formDate) : null;

    const flags = {
      is_thesis: formType === 'teza',
      is_homework: formType === 'tema',
      is_lab: formType === 'laborator',
      is_seminar: formType === 'seminar',
      is_final: formType === 'finala',
      is_conduct: formType === 'purtare',
    };

    const commonPayload = {
      gradebook_config_id: configId,
      period_index: periodIndex,
      value: isCalif ? null : Number(formValue),
      qualifier: isCalif ? formQualifier : null,
      comment: formComment.trim() || null,
      category_name: educationLevel === 'universitar' && formCategoryName.trim() ? formCategoryName.trim() : null,
      ...flags,
    };

    // IMPORTANT: backend PUT /api/grades/{id} accepta doar campuri de update (fara student_id/subject_id/classroom_id)
    const updatePayload: Record<string, any> = {
      ...commonPayload,
      ...(iso ? { date: iso } : {}),
    };

    const createPayload = {
      student_id: student.id,
      subject_id: isConduct ? null : subject?.id,
      classroom_id: classroomId,
      ...commonPayload,
      date: iso,
    };

    setBusy(true);
    try {
      if (editingGrade) {
        await api.put(`/api/grades/${editingGrade.id}`, updatePayload);
      } else {
        await api.post('/api/grades', createPayload);
      }
      await loadData();
      await onChanged();
      resetForm();
    } catch (err: any) {
      setFormError(parseApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const deleteGrade = async (g: Grade) => {
    if (!canEdit) {
      setFormError('Poti sterge note doar la materiile pe care le predai.');
      return;
    }
    if (!window.confirm('Ștergi această notă?')) return;
    setBusy(true);
    try {
      await api.delete(`/api/grades/${g.id}`);
      await loadData();
      await onChanged();
      if (editingGrade?.id === g.id) resetForm();
    } catch (err: any) {
      setFormError(parseApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const submitAbsence = async () => {
    if (!subject) return;
    setFormError(null);
    if (!canEdit) {
      setFormError('Poti adauga absente doar la materiile pe care le predai.');
      return;
    }
    if (!absenceDate) {
      setFormError('Alege data absenței.');
      return;
    }
    const iso = toBackendDateTime(absenceDate);
    if (!iso) {
      setFormError('Data absenței este invalidă.');
      return;
    }

    setBusy(true);
    try {
      await api.post('/api/absente/', {
        student_id: student.id,
        subject_id: subject.id,
        classroom_id: classroomId,
        gradebook_config_id: configId,
        period_index: periodIndex,
        date: iso,
        justified: absenceJustified ? 'da' : 'nu',
      });
      await loadData();
      await onChanged();
      resetAbsenceForm();
    } catch (err: any) {
      setFormError(parseApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const toggleJustified = async (a: Absence) => {
    setFormError(null);
    if (!isJustified(a.justified)) {
      setJustifyTarget(a);
      setJustifyReason('');
      setShowJustifyModal(true);
      return;
    }

    setBusy(true);
    try {
      await api.put(`/api/absente/${a.id}`, { justified: 'nu' });
      await loadData();
      await onChanged();
    } catch (err: any) {
      setFormError(parseApiError(err));
    } finally {
      setBusy(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[1300] flex items-center justify-center p-4 bg-black/50 animate-fade-in">
      <div className="bg-card border border-border rounded-lg shadow-lg w-full max-w-3xl max-h-[92vh] overflow-y-auto animate-scale-in">
        <div className="p-4 border-b border-border flex items-center justify-between sticky top-0 bg-card z-10">
          <div className="min-w-0">
            <h3 className="text-lg font-semibold text-foreground truncate">
              {student.full_name} — {subject ? subject.name : 'Purtare'}
            </h3>
            <p className="text-xs text-muted-foreground">
              Nivel: {educationLevel} • Structură: {structure} • Perioadă: {periodIndex + 1}
            </p>
          </div>
          <button className="p-2 rounded-md hover:bg-muted transition-colors duration-150" onClick={onClose} aria-label="Închide">
            <Icon name="X" size={18} className="text-muted-foreground" />
          </button>
        </div>

        <div className="p-4 space-y-4">
          {error && <div className="bg-error/10 border border-error/20 text-error rounded-md p-3 text-sm">{error}</div>}
          {formError && <div className="bg-error/10 border border-error/20 text-error rounded-md p-3 text-sm">{formError}</div>}
          {!canEdit && subject && (
            <div className="bg-warning/10 border border-warning/20 text-warning rounded-md p-3 text-sm">
              Poti vedea catalogul, dar poti adauga/edita note si absente doar la materiile pe care le predai.
            </div>
          )}

          <div className="bg-muted/20 border border-border rounded-lg p-4">
            <div className="flex items-center justify-between gap-2 mb-3">
              <h4 className="text-sm font-semibold text-foreground">{editingGrade ? 'Editează' : 'Adaugă'} notă</h4>
              {editingGrade && (
                <Button variant="outline" size="sm" onClick={resetForm}>
                  Renunță
                </Button>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <label className="block text-sm font-medium text-foreground">
                Tip
                <select
                  value={formType}
                  onChange={(e) => setFormType(e.target.value as GradeType)}
                  className="mt-1 w-full px-3 py-2 text-sm border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                  disabled={busy}
                >
                  {typeOptions.map((t) => (
                    <option key={t} value={t}>
                      {t === 'nota'
                        ? 'Notă'
                        : t === 'teza'
                          ? 'Teză'
                          : t === 'calificativ'
                            ? 'Calificativ'
                            : t === 'tema'
                              ? 'Temă'
                              : t === 'laborator'
                                ? 'Laborator'
                                : t === 'seminar'
                                  ? 'Seminar'
                                  : t === 'finala'
                                    ? 'Finală'
                                    : 'Purtare'}
                    </option>
                  ))}
                </select>
              </label>

              {(educationLevel === 'primar' || formType === 'calificativ') ? (
                <label className="block text-sm font-medium text-foreground">
                  Calificativ
                  <select
                    value={formQualifier}
                    onChange={(e) => setFormQualifier(e.target.value)}
                    className="mt-1 w-full px-3 py-2 text-sm border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                    disabled={busy}
                  >
                    <option value="">-- alege --</option>
                    <option value="FB">FB</option>
                    <option value="B">B</option>
                    <option value="S">S</option>
                    <option value="I">I</option>
                  </select>
                </label>
              ) : (
                <Input
                  label="Notă (1-10)"
                  type="number"
                  min={1}
                  max={10}
                  value={formValue}
                  onChange={(e) => setFormValue(e.target.value)}
                  disabled={busy}
                />
              )}

              <Input label="Data (opțional)" type="date" value={formDate} onChange={(e) => setFormDate(e.target.value)} disabled={busy} />

              {educationLevel === 'universitar' && (
                <Input
                  label="Categorie (opțional)"
                  value={formCategoryName}
                  onChange={(e) => setFormCategoryName(e.target.value)}
                  disabled={busy}
                />
              )}
            </div>

            <label className="block text-sm font-medium text-foreground mt-3">
              Observații (opțional)
              <textarea
                value={formComment}
                onChange={(e) => setFormComment(e.target.value)}
                rows={3}
                className="mt-1 w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                disabled={busy}
              />
            </label>

            <div className="flex items-center justify-end gap-2 mt-3">
              <Button variant="default" onClick={submitGrade} disabled={busy || !canEdit}>
                {editingGrade ? 'Salvează' : 'Adaugă'}
              </Button>
            </div>
          </div>

          <div className="bg-card border border-border rounded-lg p-4">
            <div className="flex items-center justify-between gap-3 mb-3">
              <h4 className="text-sm font-semibold text-foreground">Note existente</h4>
              {average != null && <div className="text-sm text-muted-foreground">Medie: {average}</div>}
            </div>
            {loading ? (
              <div className="text-sm text-muted-foreground">Se încarcă…</div>
            ) : grades.length === 0 ? (
              <div className="text-sm text-muted-foreground">Nu există note.</div>
            ) : (
              <div className="divide-y divide-border">
                {grades.map((g) => (
                  <div key={g.id} className="py-2 flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="text-sm text-foreground">{gradeLabel(g)}</div>
                      <div className="text-xs text-muted-foreground">
                        {(g.date || g.created_at) ? new Date((g.date || g.created_at) as string).toLocaleDateString() : ''}
                        {g.comment ? ` • ${g.comment}` : ''}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button variant="outline" size="sm" onClick={() => startEditGrade(g)} disabled={busy || !canEdit}>
                        Editează
                      </Button>
                      <Button variant="destructive" size="sm" onClick={() => deleteGrade(g)} disabled={busy || !canEdit}>
                        Șterge
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {subject && (
            <div className="bg-card border border-border rounded-lg p-4">
              <h4 className="text-sm font-semibold text-foreground mb-3">Absențe</h4>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
                <Input label="Data" type="date" value={absenceDate} onChange={(e) => setAbsenceDate(e.target.value)} disabled={busy} />
                <label className="flex items-center gap-2 text-sm text-foreground mt-6">
                  <input
                    type="checkbox"
                    checked={absenceJustified}
                    onChange={(e) => setAbsenceJustified(e.target.checked)}
                    className="w-4 h-4 rounded border-border text-primary focus:ring-primary"
                    disabled={busy}
                  />
                  <span>Motivată</span>
                </label>
                <div className="flex items-end">
                  <Button variant="outline" onClick={submitAbsence} disabled={busy || !canEdit} fullWidth>
                    Adaugă absență
                  </Button>
                </div>
              </div>

              {absences.length === 0 ? (
                <div className="text-sm text-muted-foreground">Nu există absențe.</div>
              ) : (
                <div className="divide-y divide-border">
                  {absences.map((a) => (
                    <div key={a.id} className="py-2 flex items-center justify-between gap-3">
                      <div className="text-sm text-foreground">
                        {(a.date ? new Date(a.date).toLocaleDateString() : '—')}{' '}
                        <span className="text-xs text-muted-foreground">{isJustified(a.justified) ? '(motivată)' : '(nemotivată)'}</span>
                        {isJustified(a.justified) && a.justification_reason && (
                          <div className="text-xs text-muted-foreground mt-1">
                            Motivare: {a.justification_reason}
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <Button variant="outline" size="sm" onClick={() => toggleJustified(a)} disabled={busy}>
                          {isJustified(a.justified) ? 'Demotivează' : 'Motivează'}
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {showJustifyModal && justifyTarget && (
            <div className="fixed inset-0 z-[1400] flex items-center justify-center p-4 bg-black/50 animate-fade-in">
              <div className="bg-card border border-border rounded-lg shadow-lg w-full max-w-xl overflow-hidden animate-scale-in">
                <div className="p-4 border-b border-border flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-foreground">Motivează absența</h3>
                  <button
                    className="p-2 rounded-md hover:bg-muted transition-colors duration-150"
                    onClick={() => setShowJustifyModal(false)}
                    aria-label="Închide"
                  >
                    <Icon name="X" size={18} className="text-muted-foreground" />
                  </button>
                </div>

                <div className="p-4 space-y-3">
                  <label className="block text-sm font-medium text-foreground">
                    Motivare *
                    <textarea
                      value={justifyReason}
                      onChange={(e) => setJustifyReason(e.target.value)}
                      rows={4}
                      className="mt-2 w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                      placeholder="Ex: scutire medicală"
                      disabled={busy}
                    />
                  </label>

                  <div className="flex items-center justify-end gap-2 pt-1">
                    <Button variant="outline" onClick={() => setShowJustifyModal(false)} disabled={busy}>
                      Anulează
                    </Button>
                    <Button
                      variant="default"
                      onClick={async () => {
                        const reason = justifyReason.trim();
                        if (!reason) {
                          setFormError('Completează motivarea (ex: scutire medicală).');
                          return;
                        }
                        setBusy(true);
                        try {
                          await api.put(`/api/absente/${justifyTarget.id}`, { justified: 'da', reason });
                          setShowJustifyModal(false);
                          await loadData();
                          await onChanged();
                        } catch (err: any) {
                          setFormError(parseApiError(err));
                        } finally {
                          setBusy(false);
                        }
                      }}
                      disabled={busy}
                    >
                      Salvează
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CatalogCellModal;
